import { Button, Card, Input, Select, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
    DateRangePicker as ZhDateRangePicker,
    Grid as ZhGrid,
    InputNumber as ZhInputNumber,
    Upload as ZhUpload,
    type UploadRequestOptions
} from "zhihao-ui-react";

import request from "@/api";
import { fileService } from "@/api/services/fileService";
import {
    loadAdvertisementAdvertOptions,
    loadAdvertisementCampaignOptions,
    type AdvertisementFormContent,
    type AdvertisementSelectorOption
} from "./AdvertisementFormPage";
import "./AdvertisementTwoFormPage.scss";

export interface AdvertisementTwoFormValues {
    positionType?: number;
    position?: number;
    displayStructure: number;
    beginTime?: string;
    endTime?: string;
    displayForm?: number;
    timeInterval?: number;
    content: AdvertisementFormContent[];
    firstDisplayForm?: number;
    secondDisplayForm?: number;
    firstTimeInterval?: number;
    secondTimeInterval?: number;
    firstContent: AdvertisementFormContent[];
    secondContent: AdvertisementFormContent[];
}

export interface AdvertisementTwoFormPageProps {
    id?: number;
    loadDetail?: (id: number) => Promise<AdvertisementTwoFormValues & { id?: number }>;
    update?: (id: number, values: Partial<AdvertisementTwoFormValues>) => Promise<unknown>;
}

interface AdvertisementUploadedFile {
    id: number;
    path?: string;
}

/** 复用 Vue twoEditType 详情接口。 */
export async function loadAdvertisementTwoDetail(id: number): Promise<AdvertisementTwoFormValues & { id?: number }> {
    const response = await request.service.get<AdvertisementTwoFormValues & { id?: number }>(
        `/dataService/admin/advertisement-position/${id}`
    );
    return response.data;
}

/** 复用 Vue twoEditType 更新接口。 */
export async function updateAdvertisementTwo(
    id: number,
    values: Partial<AdvertisementTwoFormValues>
): Promise<unknown> {
    const response = await request.service.put(`/dataService/admin/advertisement-position/${id}`, values);
    return response.data;
}

/** 复用 Vue adImg 的上传契约，使用 FormData.file 获取文件 ID 和访问路径。 */
async function uploadAdvertisementImage(file: Blob): Promise<AdvertisementUploadedFile> {
    const formData = new FormData();
    formData.append("file", file);
    const { fileService } = await import("@/api/services/fileService");
    const uploadedFile = await fileService.uploadFile(formData);
    return { id: Number(uploadedFile.id), path: uploadedFile.path };
}

const initialValues: AdvertisementTwoFormValues = {
    displayStructure: 1,
    displayForm: 1,
    timeInterval: 1,
    content: [{}],
    firstDisplayForm: 1,
    secondDisplayForm: 1,
    firstTimeInterval: 1,
    secondTimeInterval: 1,
    firstContent: [{}],
    secondContent: [{}]
};

function dateOnly(value?: string): string {
    return value?.slice(0, 10) || "";
}

function buildDateTime(value: string | undefined, endOfDay: boolean): string | undefined {
    const date = dateOnly(value);
    return date ? `${date} ${endOfDay ? "23:59:59" : "00:00:00"}` : undefined;
}

/** 用展示分支和内容索引标识上传槽位，避免两个结构写入错误的内容项。 */
function getUploadKey(displayStructure: number, slot: 1 | 2, index: number): string {
    return displayStructure === 1 ? `slot-${slot}-${index}` : `combined-${index}`;
}

/** 按 Vue twoEditType 的展示形式校验图片及轮播排序是否完整。 */
function isContentComplete(displayForm: number | undefined, content: AdvertisementFormContent[]): boolean {
    if (displayForm === 3) return true;
    if (displayForm === 1) return Boolean(content[0]?.fileId);
    return content.every(item => {
        if (!item.fileId || !item.sort) return false;
        return (
            !item.linkType ||
            item.linkType === 4 ||
            (item.linkType === 1 && item.advertId) ||
            (item.linkType === 2 && item.link) ||
            (item.linkType === 3 && item.activityCode)
        );
    });
}

/** 双展示结构表单，保持 Vue 上下两张与二合一的基础字段分支。 */
export function AdvertisementTwoFormPage({
    id: propId,
    loadDetail = loadAdvertisementTwoDetail,
    update = updateAdvertisementTwo
}: AdvertisementTwoFormPageProps) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const id = propId ?? Number(searchParams.get("id"));
    const supportsActivityJump =
        searchParams.has("positionType") && [1, 6, 11].includes(Number(searchParams.get("positionType")));
    const [values, setValues] = useState<AdvertisementTwoFormValues>(initialValues);
    const [activeTab, setActiveTab] = useState(1);
    const [loading, setLoading] = useState(id > 0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [uploadedImagePaths, setUploadedImagePaths] = useState<Record<string, string>>({});
    const [uploadingKey, setUploadingKey] = useState<string | null>(null);
    const [advertOptions, setAdvertOptions] = useState<AdvertisementSelectorOption[]>([]);
    const [campaignOptions, setCampaignOptions] = useState<AdvertisementSelectorOption[]>([]);

    useEffect(() => {
        if (!(id > 0)) {
            setLoading(false);
            return;
        }
        let disposed = false;
        void loadDetail(id)
            .then(detail => {
                if (disposed) return;
                setValues({
                    ...detail,
                    displayStructure: Number(detail.displayStructure) || 1,
                    displayForm: Number(detail.displayForm) || 1,
                    timeInterval: Number(detail.timeInterval) || 1,
                    beginTime: dateOnly(detail.beginTime),
                    endTime: dateOnly(detail.endTime),
                    content: detail.content?.length ? detail.content : [{}],
                    firstDisplayForm: Number(detail.firstDisplayForm) || 1,
                    secondDisplayForm: Number(detail.secondDisplayForm) || 1,
                    firstTimeInterval: Number(detail.firstTimeInterval) || 1,
                    secondTimeInterval: Number(detail.secondTimeInterval) || 1,
                    firstContent: detail.firstContent?.length ? detail.firstContent : [{}],
                    secondContent: detail.secondContent?.length ? detail.secondContent : [{}]
                });
                setLoading(false);
            })
            .catch(() => {
                if (disposed) return;
                setErrorMessage("广告位详情加载失败");
                setLoading(false);
            });
        return () => {
            disposed = true;
        };
    }, [id, loadDetail]);

    /** 编辑态按旧 Vue adImg 约定回显已保存的图片地址，避免只展示文件 ID。 */
    useEffect(() => {
        let disposed = false;
        const entries: Array<readonly [string, number | undefined]> = [
            ...values.firstContent.map((item, index) => [`slot-1-${index}`, item.fileId] as const),
            ...values.secondContent.map((item, index) => [`slot-2-${index}`, item.fileId] as const),
            ...values.content.map((item, index) => [`combined-${index}`, item.fileId] as const)
        ];
        const validEntries = entries.filter((entry): entry is readonly [string, number] => Number.isFinite(entry[1]));
        void Promise.all(
            validEntries.map(async ([key, fileId]) => {
                try {
                    const file = await fileService.getFileById(fileId);
                    return [key, file.path || ""] as const;
                } catch {
                    return [key, ""] as const;
                }
            })
        ).then(results => {
            if (!disposed) {
                // 回显请求失败时保留刚上传成功返回的地址，避免 fileId 更新触发的异步回显覆盖本地结果。
                setUploadedImagePaths(current => ({
                    ...current,
                    ...Object.fromEntries(results.filter(([, path]) => path))
                }));
            }
        });
        return () => {
            disposed = true;
        };
    }, [values.content, values.firstContent, values.secondContent]);

    /** 编辑态预加载跳转选项，使旧 Vue 中已选的活动/广告标题能够真实回显。 */
    useEffect(() => {
        const contents =
            values.displayStructure === 1 ? [...values.firstContent, ...values.secondContent] : values.content;
        const needsCampaign = contents.some(item => item.linkType === 3 && item.activityCode);
        const needsAdvert = contents.some(item => item.linkType === 1 && item.advertId);
        if (needsCampaign)
            void loadAdvertisementCampaignOptions("")
                .then(options =>
                    setCampaignOptions(options.map(option => ({ ...option, label: option.label.split(" | ")[0] })))
                )
                .catch(() => undefined);
        if (needsAdvert)
            void loadAdvertisementAdvertOptions("")
                .then(setAdvertOptions)
                .catch(() => undefined);
    }, [values.content, values.displayStructure, values.firstContent, values.secondContent]);

    const updateSlot = (slot: 1 | 2, patch: Partial<AdvertisementTwoFormValues>) => {
        setValues(current => {
            if (slot === 1) return { ...current, ...patch };
            return { ...current, ...patch };
        });
    };

    /** 更新当前展示结构下的单个图片内容项，统一支持跳转和排序字段。 */
    const updateContent = (slot: 1 | 2, index: number, patch: Partial<AdvertisementFormContent>) => {
        setValues(current => {
            const key = current.displayStructure === 1 ? (slot === 1 ? "firstContent" : "secondContent") : "content";
            return {
                ...current,
                [key]: current[key].map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item))
            };
        });
    };

    const addActiveContent = () => {
        setValues(current => {
            if (current.displayStructure === 1) {
                const key = activeTab === 1 ? "firstContent" : "secondContent";
                if (current[key].length >= 5) return current;
                return { ...current, [key]: [...current[key], {}] };
            }
            if (current.content.length >= 5) return current;
            return { ...current, content: [...current.content, {}] };
        });
    };

    const removeActiveContent = (index: number) => {
        setValues(current => {
            if (current.displayStructure === 1) {
                const key = activeTab === 1 ? "firstContent" : "secondContent";
                if (current[key].length <= 1) return current;
                return { ...current, [key]: current[key].filter((_, itemIndex) => itemIndex !== index) };
            }
            if (current.content.length <= 1) return current;
            return { ...current, content: current.content.filter((_, itemIndex) => itemIndex !== index) };
        });
        setUploadedImagePaths(current => {
            const prefix = values.displayStructure === 1 ? `slot-${activeTab}-` : "combined-";
            const next: Record<string, string> = {};
            Object.entries(current).forEach(([key, path]) => {
                if (!key.startsWith(prefix)) {
                    next[key] = path;
                    return;
                }
                const contentIndex = Number(key.slice(prefix.length));
                if (contentIndex < index) next[key] = path;
                if (contentIndex > index) next[`${prefix}${contentIndex - 1}`] = path;
            });
            return next;
        });
    };

    const changeSlotDisplayForm = (slot: 1 | 2, displayForm: number) => {
        setValues(current =>
            slot === 1
                ? { ...current, firstDisplayForm: displayForm, firstTimeInterval: 1, firstContent: [{}] }
                : { ...current, secondDisplayForm: displayForm, secondTimeInterval: 1, secondContent: [{}] }
        );
        setUploadedImagePaths(current => {
            const prefix = `slot-${slot}-`;
            return Object.fromEntries(Object.entries(current).filter(([key]) => !key.startsWith(prefix)));
        });
    };

    const changeCombinedDisplayForm = (displayForm: number) => {
        setValues(current => ({ ...current, displayForm, timeInterval: 1, content: [{}] }));
        setUploadedImagePaths(current =>
            Object.fromEntries(Object.entries(current).filter(([key]) => !key.startsWith("combined-")))
        );
    };

    const currentDisplayForm = activeTab === 1 ? values.firstDisplayForm : values.secondDisplayForm;
    const currentTimeInterval = activeTab === 1 ? values.firstTimeInterval : values.secondTimeInterval;
    const currentContent = activeTab === 1 ? values.firstContent : values.secondContent;

    /** 对齐 Vue UploadImg：先校验图片类型和 5MB 大小，再回填当前结构的当前内容项。 */
    const uploadContentImage = async (slot: 1 | 2, index: number, file: File) => {
        const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/avif"];
        if (!allowedTypes.includes(file.type)) {
            setErrorMessage("仅支持 JPG/PNG/GIF/WebP/AVIF 图片");
            return;
        }
        if (file.size >= 5 * 1024 * 1024) {
            setErrorMessage("图片大小不能超过 5MB");
            return;
        }

        const displayStructure = values.displayStructure;
        const uploadKey = getUploadKey(displayStructure, slot, index);
        setErrorMessage(null);
        setUploadingKey(uploadKey);
        try {
            const uploadedFile = await uploadAdvertisementImage(file);
            if (!uploadedFile.id) throw new Error("上传响应缺少文件 ID");
            setValues(current => {
                if (displayStructure === 1) {
                    const key = slot === 1 ? "firstContent" : "secondContent";
                    return {
                        ...current,
                        [key]: current[key].map((item, itemIndex) =>
                            itemIndex === index ? { ...item, fileId: uploadedFile.id } : item
                        )
                    };
                }
                return {
                    ...current,
                    content: current.content.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, fileId: uploadedFile.id } : item
                    )
                };
            });
            setUploadedImagePaths(current => ({ ...current, [uploadKey]: uploadedFile.path || "" }));
        } catch {
            setErrorMessage("广告图片上传失败");
        } finally {
            setUploadingKey(current => (current === uploadKey ? null : current));
        }
    };

    const submit = async () => {
        if (!values.beginTime || !values.endTime) {
            setErrorMessage("请选择广告周期");
            return;
        }
        const contentComplete =
            values.displayStructure === 1
                ? isContentComplete(values.firstDisplayForm, values.firstContent) &&
                  isContentComplete(values.secondDisplayForm, values.secondContent)
                : isContentComplete(values.displayForm, values.content);
        if (!contentComplete) {
            setErrorMessage("请将内容填写完整");
            return;
        }
        setErrorMessage(null);
        const payload: Partial<AdvertisementTwoFormValues> = {
            displayStructure: values.displayStructure,
            positionType: values.positionType,
            position: values.position,
            beginTime: buildDateTime(values.beginTime, false),
            endTime: buildDateTime(values.endTime, true)
        };
        if (values.displayStructure === 1) {
            payload.firstDisplayForm = values.firstDisplayForm;
            payload.secondDisplayForm = values.secondDisplayForm;
            payload.firstTimeInterval = values.firstDisplayForm === 2 ? values.firstTimeInterval : undefined;
            payload.secondTimeInterval = values.secondDisplayForm === 2 ? values.secondTimeInterval : undefined;
            payload.firstContent = values.firstDisplayForm === 3 ? undefined : values.firstContent;
            payload.secondContent = values.secondDisplayForm === 3 ? undefined : values.secondContent;
        } else {
            payload.displayForm = values.displayForm;
            payload.timeInterval = values.displayForm === 2 ? values.timeInterval : undefined;
            payload.content = values.displayForm === 3 ? undefined : values.content;
        }
        try {
            if (id > 0) await update(id, payload);
            void navigate(-1);
        } catch {
            setErrorMessage("广告位保存失败");
        }
    };

    if (loading) return <div role="status">广告位加载中...</div>;

    const displayFormOptions = [
        [1, "单图固定"],
        [2, "多图轮播"],
        [3, "不展示"]
    ] as const;

    return (
        <section
            aria-label="advertisement-two-form-page"
            className="advertisement-two-form-page flex h-full min-h-full flex-col"
        >
            <Card>
                <Typography.Title className="mb-4 mt-0" level={4}>
                    {id > 0 ? "修改广告位" : "新增广告位"}
                </Typography.Title>
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                {/* 对齐旧 Vue ad/twoEditType.vue 的四列广告位表单布局。 */}
                <ZhGrid cols={4} gap={[20, 0]}>
                    <Space className="w-full" orientation="vertical">
                        <fieldset>
                            <legend>展示结构</legend>
                            <label>
                                <input
                                    aria-label="展示结构-上下两张"
                                    checked={values.displayStructure === 1}
                                    name="displayStructure"
                                    type="radio"
                                    onChange={() => setValues(current => ({ ...current, displayStructure: 1 }))}
                                />
                                上下两张
                            </label>
                            <label>
                                <input
                                    aria-label="展示结构-二合一"
                                    checked={values.displayStructure === 2}
                                    name="displayStructure"
                                    type="radio"
                                    onChange={() => setValues(current => ({ ...current, displayStructure: 2 }))}
                                />
                                二合一
                            </label>
                        </fieldset>
                        <label className="advertisement-two-period">
                            <span>广告周期</span>
                            <ZhDateRangePicker
                                type="daterange"
                                format="YYYY-MM-DD"
                                valueFormat="YYYY-MM-DD"
                                rangeSeparator="-"
                                startPlaceholder="开始时间"
                                endPlaceholder="结束时间"
                                value={[dateOnly(values.beginTime), dateOnly(values.endTime)]}
                                onChange={value =>
                                    setValues(current => ({
                                        ...current,
                                        beginTime: String(value?.[0] || ""),
                                        endTime: String(value?.[1] || "")
                                    }))
                                }
                            />
                        </label>
                        {values.displayStructure === 1 ? (
                            <>
                                <Space>
                                    <Button
                                        aria-label="第1张"
                                        type={activeTab === 1 ? "primary" : "default"}
                                        onClick={() => setActiveTab(1)}
                                    >
                                        第1张
                                    </Button>
                                    <Button
                                        aria-label="第2张"
                                        type={activeTab === 2 ? "primary" : "default"}
                                        onClick={() => setActiveTab(2)}
                                    >
                                        第2张
                                    </Button>
                                </Space>
                                <section
                                    aria-label={`第${activeTab}张内容`}
                                    className="advertisement-two-active-section"
                                >
                                    <fieldset>
                                        <legend>展示形式</legend>
                                        {displayFormOptions.map(([code, label]) => (
                                            <label key={code}>
                                                <input
                                                    aria-label={`第${activeTab}张-展示形式-${label}`}
                                                    checked={currentDisplayForm === code}
                                                    name={`displayForm-${activeTab}`}
                                                    type="radio"
                                                    onChange={() => changeSlotDisplayForm(activeTab as 1 | 2, code)}
                                                />
                                                {label}
                                            </label>
                                        ))}
                                    </fieldset>
                                    {currentDisplayForm === 2 ? (
                                        <fieldset>
                                            <legend>轮播间隔</legend>
                                            <label>
                                                <input
                                                    aria-label={`第${activeTab}张-轮播间隔-3秒`}
                                                    checked={currentTimeInterval === 1}
                                                    name={`timeInterval-${activeTab}`}
                                                    type="radio"
                                                    onChange={() =>
                                                        updateSlot(activeTab as 1 | 2, {
                                                            [activeTab === 1
                                                                ? "firstTimeInterval"
                                                                : "secondTimeInterval"]: 1
                                                        })
                                                    }
                                                />
                                                3秒
                                            </label>
                                            <label>
                                                <input
                                                    aria-label={`第${activeTab}张-轮播间隔-5秒`}
                                                    checked={currentTimeInterval === 2}
                                                    name={`timeInterval-${activeTab}`}
                                                    type="radio"
                                                    onChange={() =>
                                                        updateSlot(activeTab as 1 | 2, {
                                                            [activeTab === 1
                                                                ? "firstTimeInterval"
                                                                : "secondTimeInterval"]: 2
                                                        })
                                                    }
                                                />
                                                5秒
                                            </label>
                                        </fieldset>
                                    ) : null}
                                    {currentDisplayForm !== 3
                                        ? currentContent.map((item, index) => (
                                              <Card
                                                  className="advertisement-two-content-card"
                                                  key={index}
                                                  size="small"
                                                  title={`第${activeTab}张图片内容 ${index + 1}`}
                                              >
                                                  <Space className="w-full" orientation="vertical">
                                                      <div className="advertisement-two-image-label">
                                                          <span>广告图片</span>
                                                          <span>支持jpg/png/webp/avif格式</span>
                                                      </div>
                                                      <ZhUpload
                                                          action=""
                                                          accept="image/jpeg,image/png,image/gif,image/webp,image/avif"
                                                          aria-label={`第${activeTab}张-上传广告图片 ${index + 1}`}
                                                          disabled={
                                                              uploadingKey ===
                                                              getUploadKey(
                                                                  values.displayStructure,
                                                                  activeTab as 1 | 2,
                                                                  index
                                                              )
                                                          }
                                                          httpRequest={(options: UploadRequestOptions) => {
                                                              void uploadContentImage(
                                                                  activeTab as 1 | 2,
                                                                  index,
                                                                  options.file
                                                              ).then(() => options.onSuccess?.({}));
                                                          }}
                                                          limit={1}
                                                          showFileList={false}
                                                      >
                                                          <div className="advertisement-two-image-upload">
                                                              {uploadedImagePaths[
                                                                  getUploadKey(
                                                                      values.displayStructure,
                                                                      activeTab as 1 | 2,
                                                                      index
                                                                  )
                                                              ] ? (
                                                                  <img
                                                                      alt={`第${activeTab}张广告图片 ${index + 1}`}
                                                                      src={
                                                                          uploadedImagePaths[
                                                                              getUploadKey(
                                                                                  values.displayStructure,
                                                                                  activeTab as 1 | 2,
                                                                                  index
                                                                              )
                                                                          ]
                                                                      }
                                                                  />
                                                              ) : (
                                                                  <span>上传轮播图</span>
                                                              )}
                                                          </div>
                                                      </ZhUpload>
                                                      {uploadedImagePaths[
                                                          getUploadKey(
                                                              values.displayStructure,
                                                              activeTab as 1 | 2,
                                                              index
                                                          )
                                                      ] ? (
                                                          <span>
                                                              图片地址：
                                                              {
                                                                  uploadedImagePaths[
                                                                      getUploadKey(
                                                                          values.displayStructure,
                                                                          activeTab as 1 | 2,
                                                                          index
                                                                      )
                                                                  ]
                                                              }
                                                          </span>
                                                      ) : null}
                                                      {/* 暴露当前内容项的文件 ID，便于表单状态同步和无障碍测试读取上传结果。 */}
                                                      <input
                                                          aria-label={`第${activeTab}张-图片文件ID ${index + 1}`}
                                                          className="sr-only"
                                                          readOnly
                                                          type="number"
                                                          value={item.fileId ?? ""}
                                                      />
                                                      <fieldset className="advertisement-two-jump-type">
                                                          <legend>跳转类型</legend>
                                                          {[1, 2, ...(supportsActivityJump ? [3] : [])].map(code => (
                                                              <label key={code}>
                                                                  <input
                                                                      aria-label={`第${activeTab}张-跳转类型-${code}`}
                                                                      checked={item.linkType === code}
                                                                      name={`jumpType-${activeTab}-${index}`}
                                                                      type="radio"
                                                                      onChange={() =>
                                                                          updateContent(activeTab as 1 | 2, index, {
                                                                              linkType: code,
                                                                              advertId: undefined,
                                                                              link: undefined,
                                                                              activityCode: undefined
                                                                          })
                                                                      }
                                                                  />
                                                                  {code === 1 ? "广告页" : code === 2 ? "链接" : "活动"}
                                                              </label>
                                                          ))}
                                                      </fieldset>
                                                      {item.linkType === 1 ? (
                                                          <label className="advertisement-two-content-field">
                                                              <span>广告页</span>
                                                              <Select
                                                                  aria-label={`第${activeTab}张-广告页 ${index + 1}`}
                                                                  options={advertOptions}
                                                                  placeholder="请搜索广告页标题或编号选择"
                                                                  value={item.advertId ?? undefined}
                                                                  onChange={advertId =>
                                                                      updateContent(activeTab as 1 | 2, index, {
                                                                          advertId: Number(advertId)
                                                                      })
                                                                  }
                                                              />
                                                          </label>
                                                      ) : null}
                                                      {item.linkType === 2 ? (
                                                          <label className="advertisement-two-content-field">
                                                              <span>跳转链接</span>
                                                              <Input
                                                                  aria-label={`第${activeTab}张-跳转链接 ${index + 1}`}
                                                                  maxLength={64}
                                                                  placeholder="请输入跳转链接"
                                                                  value={item.link || ""}
                                                                  onChange={event =>
                                                                      updateContent(activeTab as 1 | 2, index, {
                                                                          link: event.target.value
                                                                      })
                                                                  }
                                                              />
                                                          </label>
                                                      ) : null}
                                                      {item.linkType === 3 ? (
                                                          <label className="advertisement-two-content-field">
                                                              <span>活动</span>
                                                              <Select
                                                                  aria-label={`第${activeTab}张-活动 ${index + 1}`}
                                                                  options={campaignOptions}
                                                                  placeholder="请搜索活动名称或活动id"
                                                                  value={item.activityCode || undefined}
                                                                  onChange={activityCode =>
                                                                      updateContent(activeTab as 1 | 2, index, {
                                                                          activityCode: String(activityCode)
                                                                      })
                                                                  }
                                                              />
                                                          </label>
                                                      ) : null}
                                                      {currentDisplayForm === 2 ? (
                                                          <label className="advertisement-two-content-field advertisement-two-sort-field">
                                                              <span>排序</span>
                                                              <ZhInputNumber
                                                                  aria-label={`第${activeTab}张-排序 ${index + 1}`}
                                                                  max={5}
                                                                  min={1}
                                                                  placeholder="请输入数字"
                                                                  precision={0}
                                                                  value={item.sort ?? ""}
                                                                  onChange={sort =>
                                                                      updateContent(activeTab as 1 | 2, index, {
                                                                          sort: Number(sort) || undefined
                                                                      })
                                                                  }
                                                              />
                                                              <small>
                                                                  输入1~5之间数字，数字越大，权重越高；优先显示权重高的广告
                                                              </small>
                                                          </label>
                                                      ) : null}
                                                  </Space>
                                                  {currentContent.length > 1 ? (
                                                      <Button
                                                          aria-label={`第${activeTab}张删除图片内容 ${index + 1}`}
                                                          onClick={() => removeActiveContent(index)}
                                                      >
                                                          删除
                                                      </Button>
                                                  ) : null}
                                              </Card>
                                          ))
                                        : null}
                                    {currentDisplayForm === 2 && currentContent.length < 5 ? (
                                        <Button aria-label={`第${activeTab}张新增图片内容`} onClick={addActiveContent}>
                                            新增
                                        </Button>
                                    ) : null}
                                </section>
                            </>
                        ) : (
                            <section aria-label="二合一内容">
                                <fieldset>
                                    <legend>二合一展示形式</legend>
                                    {displayFormOptions.map(([code, label]) => (
                                        <label key={code}>
                                            <input
                                                aria-label={`二合一-展示形式-${label}`}
                                                checked={values.displayForm === code}
                                                name="combinedDisplayForm"
                                                type="radio"
                                                onChange={() => changeCombinedDisplayForm(code)}
                                            />
                                            {label}
                                        </label>
                                    ))}
                                </fieldset>
                                {values.displayForm === 2 ? (
                                    <fieldset>
                                        <legend>二合一轮播间隔</legend>
                                        <label>
                                            <input
                                                aria-label="二合一-轮播间隔-3秒"
                                                checked={values.timeInterval === 1}
                                                name="combinedTimeInterval"
                                                type="radio"
                                                onChange={() => setValues(current => ({ ...current, timeInterval: 1 }))}
                                            />
                                            3秒
                                        </label>
                                        <label>
                                            <input
                                                aria-label="二合一-轮播间隔-5秒"
                                                checked={values.timeInterval === 2}
                                                name="combinedTimeInterval"
                                                type="radio"
                                                onChange={() => setValues(current => ({ ...current, timeInterval: 2 }))}
                                            />
                                            5秒
                                        </label>
                                    </fieldset>
                                ) : null}
                                {values.displayForm !== 3
                                    ? values.content.map((item, index) => (
                                          <Card
                                              className="advertisement-two-content-card"
                                              key={index}
                                              size="small"
                                              title={`二合一图片内容 ${index + 1}`}
                                          >
                                              <Space className="w-full" orientation="vertical">
                                                  <div className="advertisement-two-image-label">
                                                      <span>广告图片</span>
                                                      <span>支持jpg/png/webp/avif格式</span>
                                                  </div>
                                                  <ZhUpload
                                                      action=""
                                                      accept="image/jpeg,image/png,image/gif,image/webp,image/avif"
                                                      aria-label={`二合一-上传广告图片 ${index + 1}`}
                                                      disabled={
                                                          uploadingKey ===
                                                          getUploadKey(values.displayStructure, 1, index)
                                                      }
                                                      httpRequest={(options: UploadRequestOptions) => {
                                                          void uploadContentImage(1, index, options.file).then(() =>
                                                              options.onSuccess?.({})
                                                          );
                                                      }}
                                                      limit={1}
                                                      showFileList={false}
                                                  >
                                                      <div className="advertisement-two-image-upload">
                                                          {uploadedImagePaths[
                                                              getUploadKey(values.displayStructure, 1, index)
                                                          ] ? (
                                                              <img
                                                                  alt={`二合一广告图片 ${index + 1}`}
                                                                  src={
                                                                      uploadedImagePaths[
                                                                          getUploadKey(
                                                                              values.displayStructure,
                                                                              1,
                                                                              index
                                                                          )
                                                                      ]
                                                                  }
                                                              />
                                                          ) : (
                                                              <span>上传轮播图</span>
                                                          )}
                                                      </div>
                                                  </ZhUpload>
                                                  {uploadedImagePaths[
                                                      getUploadKey(values.displayStructure, 1, index)
                                                  ] ? (
                                                      <span>
                                                          图片地址：
                                                          {
                                                              uploadedImagePaths[
                                                                  getUploadKey(values.displayStructure, 1, index)
                                                              ]
                                                          }
                                                      </span>
                                                  ) : null}
                                                  {/* 暴露当前内容项的文件 ID，便于表单状态同步和无障碍测试读取上传结果。 */}
                                                  <input
                                                      aria-label={`二合一-图片文件ID ${index + 1}`}
                                                      className="sr-only"
                                                      readOnly
                                                      type="number"
                                                      value={item.fileId ?? ""}
                                                  />
                                                  <fieldset className="advertisement-two-jump-type">
                                                      <legend>跳转类型</legend>
                                                      {[1, 2, ...(supportsActivityJump ? [3] : [])].map(code => (
                                                          <label key={code}>
                                                              <input
                                                                  aria-label={`二合一-跳转类型-${code}`}
                                                                  checked={item.linkType === code}
                                                                  name={`combinedJumpType-${index}`}
                                                                  type="radio"
                                                                  onChange={() =>
                                                                      updateContent(1, index, {
                                                                          linkType: code,
                                                                          advertId: undefined,
                                                                          link: undefined,
                                                                          activityCode: undefined
                                                                      })
                                                                  }
                                                              />
                                                              {code === 1 ? "广告页" : code === 2 ? "链接" : "活动"}
                                                          </label>
                                                      ))}
                                                  </fieldset>
                                                  {item.linkType === 1 ? (
                                                      <label className="advertisement-two-content-field">
                                                          <span>广告页</span>
                                                          <Select
                                                              aria-label={`二合一-广告页 ${index + 1}`}
                                                              options={advertOptions}
                                                              placeholder="请搜索广告页标题或编号选择"
                                                              value={item.advertId ?? undefined}
                                                              onChange={advertId =>
                                                                  updateContent(1, index, {
                                                                      advertId: Number(advertId)
                                                                  })
                                                              }
                                                          />
                                                      </label>
                                                  ) : null}
                                                  {item.linkType === 2 ? (
                                                      <label className="advertisement-two-content-field">
                                                          <span>跳转链接</span>
                                                          <Input
                                                              aria-label={`二合一-跳转链接 ${index + 1}`}
                                                              maxLength={64}
                                                              placeholder="请输入跳转链接"
                                                              value={item.link || ""}
                                                              onChange={event =>
                                                                  updateContent(1, index, { link: event.target.value })
                                                              }
                                                          />
                                                      </label>
                                                  ) : null}
                                                  {item.linkType === 3 ? (
                                                      <label className="advertisement-two-content-field">
                                                          <span>活动</span>
                                                          <Select
                                                              aria-label={`二合一-活动 ${index + 1}`}
                                                              options={campaignOptions}
                                                              placeholder="请搜索活动名称或活动id"
                                                              value={item.activityCode || undefined}
                                                              onChange={activityCode =>
                                                                  updateContent(1, index, {
                                                                      activityCode: String(activityCode)
                                                                  })
                                                              }
                                                          />
                                                      </label>
                                                  ) : null}
                                                  {values.displayForm === 2 ? (
                                                      <label className="advertisement-two-content-field advertisement-two-sort-field">
                                                          <span>排序</span>
                                                          <ZhInputNumber
                                                              aria-label={`二合一-排序 ${index + 1}`}
                                                              max={5}
                                                              min={1}
                                                              placeholder="请输入数字"
                                                              precision={0}
                                                              value={item.sort ?? ""}
                                                              onChange={sort =>
                                                                  updateContent(1, index, {
                                                                      sort: Number(sort) || undefined
                                                                  })
                                                              }
                                                          />
                                                          <small>
                                                              输入1~5之间数字，数字越大，权重越高；优先显示权重高的广告
                                                          </small>
                                                      </label>
                                                  ) : null}
                                              </Space>
                                              {values.content.length > 1 ? (
                                                  <Button
                                                      aria-label={`二合一删除图片内容 ${index + 1}`}
                                                      onClick={() => removeActiveContent(index)}
                                                  >
                                                      删除
                                                  </Button>
                                              ) : null}
                                          </Card>
                                      ))
                                    : null}
                                {values.displayForm === 2 && values.content.length < 5 ? (
                                    <Button aria-label="二合一新增图片内容" onClick={addActiveContent}>
                                        新增
                                    </Button>
                                ) : null}
                            </section>
                        )}
                        <Space className="operation-fixed-btn">
                            <Button aria-label="确定" type="primary" onClick={() => void submit()}>
                                确定
                            </Button>
                            <Button aria-label="返回" onClick={() => void navigate(-1)}>
                                返回
                            </Button>
                        </Space>
                    </Space>
                </ZhGrid>
            </Card>
        </section>
    );
}

export default AdvertisementTwoFormPage;
